import { useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchVerification, decideVerification, type AdminVerification } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow } from '@/components/DataDisplay'
import { StatusBadge } from '@/components/StatusBadge'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'

/**
 * A9.02/06 — Verifikatsiya ariza kartasi + qaror. GET /admin/verification/:id,
 * POST /admin/verification/:id/decide (backend `663ef50`). APPROVE => is_official.
 */
export function VerificationCard() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  const { id = '' } = useParams()
  const [req, setReq] = useState<AdminVerification | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchVerification(Number(id), ac.signal)
      .then((r) => {
        if (alive) setReq(r)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id, reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const decide = async (approve: boolean) => {
    setBusy(true)
    try {
      await decideVerification(Number(id), approve, note)
      toast(approve ? t.verif.approvedToast : t.verif.rejectedToast, { tone: 'success' })
      navigate('/verification')
    } catch (e) {
      toast(t.verif.decideFailed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
      setBusy(false)
    }
  }

  const kindLabel = (k: string) =>
    k === 'org' ? t.verif.kindOrg : k === 'notable' ? t.verif.kindNotable : t.verif.kindIdentity

  return (
    <>
      <PageHeader
        title={req ? `#${req.id} · ${req.kind}` : t.verif.card}
        screenId="A9.02"
        actions={
          <Link
            to="/verification"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.verif.queue}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-4 w-2/3" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !req ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <div className="max-w-xl space-y-3">
            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="mb-3">
                <StatusBadge status={req.status === 'pending' ? 'pending' : req.status === 'approved' ? 'active' : 'deleted'} />
              </div>
              <Field label={t.verif.kind}>{kindLabel(req.kind)}</Field>
              <Field label={t.col.name}>
                <Link to={`/users/${req.user_id}`} className="text-accent hover:underline">
                  {req.username ? `@${req.username}` : `#${req.user_id}`}
                </Link>
              </Field>
              <Field label={t.verif.evidence}>
                <span className="whitespace-pre-wrap">{req.evidence || '—'}</span>
              </Field>
              {req.status !== 'pending' && <Field label={t.appeals.decisionNote}>{req.decision_note || '—'}</Field>}
            </div>

            {req.status === 'pending' && can(PERMISSIONS.USERS_BAN) && (
              <div className="rounded-lg border border-border-default bg-surface-2 p-4">
                <div className="mb-2 text-sm font-medium text-secondary">{t.appeals.decision}</div>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t.appeals.notePlaceholder}
                  rows={2}
                  className="mb-3 w-full rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
                />
                <div className="flex gap-2">
                  <button
                    disabled={busy}
                    onClick={() => decide(false)}
                    className="rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-secondary hover:text-primary disabled:opacity-50"
                  >
                    {t.verif.reject}
                  </button>
                  <button
                    disabled={busy}
                    onClick={() => decide(true)}
                    className="rounded-md border border-success bg-success-subtle px-3 py-1.5 text-sm text-success hover:bg-success hover:text-white disabled:opacity-50"
                  >
                    {t.verif.approve}
                  </button>
                </div>
                <p className="mt-2 text-xs text-tertiary">{t.verif.approveHint}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <InfoRow label={label}>{children}</InfoRow>
}
